import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Plus,
  Eye,
  PencilLine,
  Check,
  XCircle,
  Inbox,
  Users,
  Wallet,
  TrendingDown,
  Shield,
  Target,
  FileText,
  X,
  ChevronRight,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import AdvisorConnectModal, {
  ADVISOR_MODULE_OPTIONS,
} from "../components/advisor/AdvisorConnectModal";
import {
  KeyFactsRow,
  ChangeRequestDetail,
} from "../components/advisor/ChangeRequestViews";

const MODULE_LABELS = Object.fromEntries(
  ADVISOR_MODULE_OPTIONS.map((m) => [m.key, m.label]),
);

export default function AccessManagement() {
  const [tab, setTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "requests"
      ? "requests"
      : "advisors",
  );
  const [connections, setConnections] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Connect-new flow
  const [code, setCode] = useState("");
  const [pendingAdvisor, setPendingAdvisor] = useState(null);
  const [connecting, setConnecting] = useState(false);

  // Approvals
  const [deciding, setDeciding] = useState(null);
  const [detailReq, setDetailReq] = useState(null);
  const [rejectReq, setRejectReq] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/connections"),
        api.get("/connections/requests?status=pending"),
      ]);
      setConnections(c.data.connections || []);
      setRequests(r.data.requests || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("pfa:approvals-changed", onChange);
    window.addEventListener("focus", onChange);
    return () => {
      window.removeEventListener("pfa:approvals-changed", onChange);
      window.removeEventListener("focus", onChange);
    };
  }, [load]);

  const validateCode = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/connections/validate-code", {
        advisor_code: code.trim(),
      });
      if (data.already_connected) {
        toast.info("You're already connected to this advisor");
        return;
      }
      setPendingAdvisor(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirmConnect = async ({ modules, permission_level, shared_ids }) => {
    setConnecting(true);
    try {
      await api.post("/connections", {
        advisor_code: pendingAdvisor.advisor_code,
        modules,
        permission_level,
        shared_ids,
      });
      toast.success("Advisor connected");
      setPendingAdvisor(null);
      setCode("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConnecting(false);
    }
  };

  const decide = async (req, approve, reason) => {
    setDeciding(req.id);
    try {
      await api.post(
        `/connections/requests/${req.id}/${approve ? "approve" : "reject"}`,
        approve ? {} : { client_note: reason },
      );
      setRequests((rs) => rs.filter((r) => r.id !== req.id));
      window.dispatchEvent(new CustomEvent("pfa:approvals-changed"));
      setDetailReq(null);
      setRejectReq(null);
      toast.success(
        approve ? "Change approved & applied" : "Request rejected",
        approve
          ? {
              description: `Your ${MODULE_LABELS[req.module] || req.module} have been updated.`,
            }
          : { description: "Your advisor has been notified." },
      );
    } catch (err) {
      toast.error(formatApiError(err));
      await load();
    } finally {
      setDeciding(null);
    }
  };

  const activeConns = connections.filter((c) => c.status === "active");
  const pendingByConn = (cid) =>
    requests.filter((r) => r.connection_id === cid).length;

  return (
    <DashboardLayout>
      <div className=" mx-auto mb-8">
        {/* HEADER */}
        <div className="flex items-center gap-3 mb-1">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
              Access Management
            </h1>
            <p className="text-sm text-slate-500">
              Your Personal Finance Advisors — what they see, what they can do.
            </p>
          </div>
        </div>

        {/* PENDING ALERT */}
        {requests.length > 0 && tab !== "requests" && (
          <button
            onClick={() => setTab("requests")}
            className="mt-5 w-full flex items-center gap-3 rounded-2xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-left hover:bg-amber-100 dark:hover:bg-amber-500/15 transition"
          >
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-amber-400 text-slate-900">
              <Inbox className="h-4 w-4" />
            </span>
            <span className="flex-1 text-sm font-medium text-amber-800 dark:text-amber-200">
              {requests.length} change request{requests.length > 1 ? "s" : ""}{" "}
              need your review.
            </span>
            <span className="text-xs font-bold text-amber-700 dark:text-amber-300">
              Review →
            </span>
          </button>
        )}

        {/* TABS */}
        <div className="mt-6 flex gap-1 border-b border-slate-200 dark:border-slate-800">
          <TabBtn
            active={tab === "advisors"}
            onClick={() => setTab("advisors")}
            icon={Users}
            label={`My advisors (${activeConns.length})`}
          />
          <TabBtn
            active={tab === "requests"}
            onClick={() => setTab("requests")}
            icon={Inbox}
            label={`Approvals (${requests.length})`}
          />
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">Loading…</div>
        ) : tab === "advisors" ? (
          <div className="mt-6 space-y-5">
            {/* CONNECT NEW */}
            <div className="rounded-2xl border border-dashed border-emerald-300 dark:border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-500/[0.06] p-5">
              <div className="text-sm font-semibold text-slate-900 dark:text-white mb-1">
                Connect with an advisor
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Enter their code — you choose exactly what to share next.
              </p>
              <div className="flex gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().trim())}
                  placeholder="PFA-XXXXXXXX"
                  className="input-soft font-mono flex-1"
                />
                <button
                  onClick={validateCode}
                  disabled={!code.trim()}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Connect
                </button>
              </div>
            </div>

            {/* LIST */}
            {activeConns.length === 0 ? (
              <div className="rounded-3xl border border-slate-200 dark:border-slate-800 p-12 text-center text-slate-400">
                You haven't connected with any advisor yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {activeConns.map((conn) => (
                  <AdvisorListCard
                    key={conn.id}
                    conn={conn}
                    pending={pendingByConn(conn.id)}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {requests.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center">
                <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500">
                  <Check className="h-6 w-6" />
                </div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  You're all caught up
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  No change requests waiting for your approval.
                </p>
              </div>
            ) : (
              requests.map((req) => (
                <RequestCard
                  key={req.id}
                  req={req}
                  busy={deciding === req.id}
                  onOpen={() => setDetailReq(req)}
                  onApprove={() => decide(req, true)}
                  onReject={() => setRejectReq(req)}
                />
              ))
            )}
          </div>
        )}
      </div>

      {/* CONNECT MODAL */}
      {pendingAdvisor && (
        <AdvisorConnectModal
          advisor={pendingAdvisor}
          allowRecordSelection
          confirmLabel={connecting ? "Connecting…" : "Connect with advisor"}
          onCancel={() => setPendingAdvisor(null)}
          onConfirm={confirmConnect}
        />
      )}

      {/* REQUEST DETAIL MODAL */}
      {detailReq && (
        <RequestDetailModal
          req={detailReq}
          busy={deciding === detailReq.id}
          onClose={() => setDetailReq(null)}
          onApprove={() => decide(detailReq, true)}
          onReject={() => setRejectReq(detailReq)}
        />
      )}

      {/* REJECT REASON MODAL */}
      {rejectReq && (
        <RejectModal
          req={rejectReq}
          busy={deciding === rejectReq.id}
          onCancel={() => setRejectReq(null)}
          onConfirm={(reason) => decide(rejectReq, false, reason)}
        />
      )}
    </DashboardLayout>
  );
}

function TabBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
        active
          ? "border-emerald-500 text-emerald-700 dark:text-emerald-300"
          : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      }`}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

// Premium summary card → opens the dedicated advisor detail page.
function AdvisorListCard({ conn, pending }) {
  const name = conn.advisor_display_name || conn.advisor_name || "Advisor";
  const modules = ADVISOR_MODULE_OPTIONS.filter((m) => conn.modules?.[m.key]);
  const canSuggest = conn.permission_level === "suggest";
  return (
    <Link
      to={`/dashboard/access/${conn.id}`}
      className="group rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 hover:border-emerald-300 dark:hover:border-emerald-500/40 hover:shadow-md transition flex flex-col"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 text-white text-lg font-bold">
          {name[0]?.toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 dark:text-white truncate">
            {name}
          </div>
          {conn.advisor_firm && (
            <div className="text-xs text-slate-500 truncate">
              {conn.advisor_firm}
            </div>
          )}
          <div className="text-[11px] font-mono text-slate-400 mt-0.5">
            {conn.advisor_code}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-emerald-500 transition" />
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span
          className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
            canSuggest
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
              : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          }`}
        >
          {canSuggest ? (
            <PencilLine className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
          {canSuggest ? "View + Edit" : "View only"}
        </span>
        <span className="text-[11px] text-slate-400">
          {modules.length} module{modules.length === 1 ? "" : "s"} shared
        </span>
      </div>

      {pending > 0 && (
        <div className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[11px] font-bold px-2.5 py-1">
          <Inbox className="h-3 w-3" />
          {pending} awaiting your approval
        </div>
      )}
    </Link>
  );
}

// ---- Approval cards (shared visuals) ----
const MODULE_META = {
  assets: { label: "Assets", cls: "bg-emerald-500 text-white", icon: Wallet },
  liabilities: {
    label: "Liabilities",
    cls: "bg-rose-500 text-white",
    icon: TrendingDown,
  },
  insurance: {
    label: "Insurance",
    cls: "bg-teal-500 text-white",
    icon: Shield,
  },
  goals: { label: "Goals", cls: "bg-amber-500 text-white", icon: Target },
  documents: {
    label: "Documents",
    cls: "bg-orange-500 text-white",
    icon: FileText,
  },
  dashboard: {
    label: "Dashboard",
    cls: "bg-slate-600 text-white",
    icon: Wallet,
  },
};

const OP_META = {
  create: {
    label: "Add",
    cls: "text-emerald-700 bg-emerald-100 dark:bg-emerald-500/15 dark:text-emerald-300",
    bar: "bg-emerald-400",
  },
  update: {
    label: "Edit",
    cls: "text-teal-700 bg-teal-100 dark:bg-teal-500/15 dark:text-teal-300",
    bar: "bg-teal-400",
  },
  delete: {
    label: "Remove",
    cls: "text-rose-700 bg-rose-100 dark:bg-rose-500/15 dark:text-rose-300",
    bar: "bg-rose-400",
  },
};

const HIDDEN_FIELDS = ["id", "user_id", "created_at", "updated_at"];

function reqTitle(req) {
  return (
    (req.payload && (req.payload.name || req.payload.title)) ||
    (req.before && (req.before.name || req.before.title)) ||
    req.summary ||
    `${MODULE_LABELS[req.module] || req.module} record`
  );
}

function changedCount(req) {
  const after = req.payload || {};
  const before = req.before || {};
  const keys = Object.keys(after).filter((k) => !HIDDEN_FIELDS.includes(k));
  if (req.operation === "delete") return 0;
  if (req.operation === "create") return keys.length;
  return keys.filter((k) => String(before[k]) !== String(after[k])).length;
}

function RequestCard({ req, onOpen, onApprove, onReject, busy }) {
  const op = OP_META[req.operation] || OP_META.update;
  const mod = MODULE_META[req.module] || MODULE_META.dashboard;
  const ModIcon = mod.icon;
  const n = changedCount(req);
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition">
      <span className={`absolute left-0 inset-y-0 w-1.5 ${op.bar}`} />
      <div className="p-4 pl-5 flex items-center gap-4">
        <div className="hidden sm:flex flex-col items-center gap-1 w-20 shrink-0">
          <span
            className={`grid h-11 w-11 place-items-center rounded-2xl ${mod.cls}`}
          >
            <ModIcon className="h-5 w-5" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
            {mod.label}
          </span>
        </div>
        <button onClick={onOpen} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${op.cls}`}
            >
              {op.label}
            </span>
            <span className="text-base font-semibold text-slate-900 dark:text-white truncate">
              {reqTitle(req)}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {req.advisor_name} ·{" "}
            {req.operation === "delete"
              ? "wants to remove this record"
              : req.operation === "create"
                ? `${n} field${n === 1 ? "" : "s"} to add`
                : `${n} field${n === 1 ? "" : "s"} changed`}
            {" · "}
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">
              View details
            </span>
          </div>
          <KeyFactsRow req={req} />
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onReject}
            disabled={busy}
            title="Reject"
            className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 dark:border-slate-700 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50"
          >
            <XCircle className="h-5 w-5" />
          </button>
          <button
            onClick={onApprove}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-4 h-10 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            <span className="hidden sm:inline">{busy ? "…" : "Approve"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestDetailModal({ req, onClose, onApprove, onReject, busy }) {
  const op = OP_META[req.operation] || OP_META.update;
  const mod = MODULE_META[req.module] || MODULE_META.dashboard;
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <span
              className={`grid h-10 w-10 place-items-center rounded-2xl ${mod.cls}`}
            >
              <mod.icon className="h-5 w-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${op.cls}`}
                >
                  {op.label}
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {mod.label}
                </span>
              </div>
              <div className="text-xs text-slate-500">{reqTitle(req)}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5 max-h-[55vh] overflow-y-auto">
          <div className="text-xs text-slate-500 mb-3">
            Proposed by <b>{req.advisor_name}</b> ·{" "}
            {new Date(req.created_at).toLocaleString()}
          </div>
          {req.advisor_note && (
            <div className="mb-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
              <span className="text-slate-400">Advisor's note: </span>
              {req.advisor_note}
            </div>
          )}
          <ChangeRequestDetail req={req} />
        </div>
        <div className="p-5 border-t border-slate-200 dark:border-slate-800 flex gap-2 justify-end">
          <button
            onClick={onReject}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-semibold px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <XCircle className="h-4 w-4" /> Reject
          </button>
          <button
            onClick={onApprove}
            disabled={busy}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-50"
          >
            <Check className="h-4 w-4" />{" "}
            {busy ? "Applying…" : "Approve & apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RejectModal({ req, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-slate-200 dark:border-slate-800">
          <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
            Reject this change?
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Let {req.advisor_name} know why — they'll see your reason.
          </p>
        </div>
        <div className="p-5">
          <textarea
            autoFocus
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The value is incorrect, please re-check."
            className="input-soft"
          />
        </div>
        <div className="p-5 border-t border-slate-200 dark:border-slate-800 flex gap-2 justify-end">
          <button onClick={onCancel} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason.trim())}
            disabled={busy || !reason.trim()}
            className="flex items-center gap-1.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold px-5 py-2.5 disabled:opacity-50"
          >
            <XCircle className="h-4 w-4" /> {busy ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </div>
  );
}

